import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, PROFILE_KEY, REFRESH_KEY, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

const storeTokens = (data) => {
  const token = data.access_token || data.session_token;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me while returning from Google OAuth; AuthCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) return;
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const out = () => setUser((u) => (u ? false : u));
    window.addEventListener("aetryx:logout", out);
    return () => window.removeEventListener("aetryx:logout", out);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    storeTokens(data);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    storeTokens(data);
    setUser(data.user);
    return data.user;
  };

  const completeGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    storeTokens(data);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    [TOKEN_KEY, REFRESH_KEY, PROFILE_KEY].forEach((k) => localStorage.removeItem(k));
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, completeGoogle, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
